// Sidebar panel for entering chemistry formulas in the RealChem DSL.
// Parses the formula, renders LaTeX inline, lets the user materialise
// the reaction as a tiny experiment, save it to their library, or
// share it publicly. The textarea is fully controlled — App owns
// the source string so the LibraryPanel can write a saved formula
// back into it.

import { useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../auth/client";
import { useAuth } from "../auth/AuthProvider";
import { parseFormula } from "../formula/parse";
import type { Formula as FormulaT, MaterialResolver } from "../formula/types";
import type { SandboxLike } from "../sim/types";

import { Formula } from "./Formula";

// Rotating placeholder examples for the Text-to-Formula textbox.
const FORMULA_PROMPT_EXAMPLES = [
  "burn methane in oxygen at 1100 K",
  "dissolve sodium hydroxide in hydrochloric acid",
  "freeze water into ice",
  "smelt iron oxide with carbon in a furnace",
  "decompose hydrogen peroxide into water and oxygen",
  "react sodium with chlorine to make table salt",
];

export interface FormulaPanelProps {
  resolver: MaterialResolver;
  sandbox: SandboxLike | null;
  /** Controlled value for the formula source textarea. App owns this
   * so LibraryPanel can drop a saved formula straight in. */
  source: string;
  onSourceChange: (next: string) => void;
  /** Open the auth modal — called from the Save button when the user
   * isn't signed in. Wired through App so the modal is a single
   * top-level mount. */
  onOpenAuth: (mode: "signin" | "signup") => void;
}

export function FormulaPanel({
  resolver,
  sandbox,
  source,
  onSourceChange,
  onOpenAuth,
}: FormulaPanelProps) {
  const { user } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  // Public-share opt-in, off by default. Sticky across saves so a
  // user who's been sharing can keep sharing without re-checking
  // the box every time.
  const [makePublic, setMakePublic] = useState(false);
  // Text-to-Formula state.
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [exampleIdx, setExampleIdx] = useState(0);

  useEffect(() => {
    if (prompt.length > 0) return;
    const id = setInterval(() => {
      setExampleIdx((i) => (i + 1) % FORMULA_PROMPT_EXAMPLES.length);
    }, 5000);
    return () => clearInterval(id);
  }, [prompt.length]);

  const placeholder =
    FORMULA_PROMPT_EXAMPLES[exampleIdx] ??
    FORMULA_PROMPT_EXAMPLES[0] ??
    "describe a reaction...";

  const parsed = useMemo<FormulaT | null>(() => {
    try {
      return parseFormula(source);
    } catch {
      return null;
    }
  }, [source]);

  const runFormula = () => {
    if (!sandbox || !parsed) return;
    const w = sandbox.width();
    const h = sandbox.height();
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h / 2);

    const reactants = parsed.reactants;
    if (reactants.length === 0) {
      setStatus("formula has no reactants");
      return;
    }
    // Lay each reactant down as a small circular blob, spaced along x.
    const blobR = 4;
    const spacing = blobR * 3;
    const startX = cx - ((reactants.length - 1) * spacing) / 2;
    const unknown: string[] = [];
    reactants.forEach((term, i) => {
      const id = sandbox.materialId(term.id);
      if (id === 0) {
        unknown.push(term.id);
        return;
      }
      const x = Math.round(startX + i * spacing);
      sandbox.paintCircle(x, cy, blobR, id);
    });
    if (unknown.length > 0) {
      setStatus(`unknown materials: ${unknown.join(", ")}`);
      return;
    }

    if (parsed.conditions.temperatureK !== undefined) {
      sandbox.heatCircle(
        cx,
        cy,
        Math.max(8, blobR * reactants.length),
        parsed.conditions.temperatureK,
      );
    }

    const ticks = parsed.conditions.ticks ?? 0;
    if (ticks > 0) {
      // Run synchronously so the user sees the outcome immediately. The
      // dt matches the main render loop.
      for (let i = 0; i < ticks; i++) sandbox.tick(0.016);
    }
    setStatus(`placed ${reactants.length} reactant blob(s)${ticks ? `, ran ${ticks} ticks` : ""}`);
  };

  const saveFormula = async () => {
    setStatus(null);
    if (!user) {
      onOpenAuth("signup");
      setStatus("sign in to save formulas to your library");
      return;
    }
    try {
      await api.saveFormula(source, makePublic);
      setStatus(makePublic ? "saved to your library (public)" : "saved to your library");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onOpenAuth("signin");
        setStatus("session expired — sign in again");
      } else {
        setStatus(`save failed: ${err instanceof Error ? err.message : "unknown error"}`);
      }
    }
  };

  const generate = async () => {
    if (prompt.trim().length === 0) return;
    setAiError(null);
    if (!user) {
      onOpenAuth("signup");
      setAiError("Sign in to use the AI assistant.");
      return;
    }
    setGenerating(true);
    try {
      const { source: generated } = await api.textToFormula(prompt.trim());
      onSourceChange(generated);
      setStatus("generated; review the LaTeX, then click Run formula");
    } catch (err) {
      if (err instanceof ApiError && err.code === "ai_unavailable") {
        setAiError("AI is not configured on this deploy (ANTHROPIC_API_KEY missing).");
      } else if (err instanceof ApiError && err.status === 401) {
        onOpenAuth("signin");
        setAiError("Session expired — sign in again.");
      } else {
        setAiError(err instanceof Error ? err.message : "AI request failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="formula-panel">
      <h2>Formula experiment</h2>
      <textarea
        className="formula-input"
        rows={3}
        value={source}
        onChange={(e) => {
          onSourceChange(e.target.value);
          setStatus(null);
        }}
        spellCheck={false}
      />
      <Formula source={source} resolver={resolver} />
      <div className="panel-actions">
        <button
          type="button"
          className="formula-run"
          onClick={runFormula}
          disabled={!sandbox || !parsed}
        >
          Run formula
        </button>
        <button
          type="button"
          className="formula-secondary"
          onClick={saveFormula}
          title={user ? "Save to your library" : "Sign in to save formulas"}
        >
          Save formula
        </button>
        <label className="public-toggle" title="Show this formula in Public Formulas after saving">
          <input
            type="checkbox"
            checked={makePublic}
            onChange={(e) => setMakePublic(e.target.checked)}
          />
          <span>Public</span>
        </label>
      </div>
      {status && <div className="formula-status">{status}</div>}
      <p className="formula-help">
        Syntax: <code>n material + ... -&gt; n material @ T=K, ticks=N</code>
      </p>

      <h2 style={{ marginTop: 16 }}>Text-to-Formula</h2>
      <textarea
        className="formula-input"
        rows={2}
        value={prompt}
        placeholder={placeholder}
        onChange={(e) => {
          setPrompt(e.target.value);
          setAiError(null);
        }}
        spellCheck={false}
      />
      <button
        type="button"
        className="formula-run"
        onClick={generate}
        disabled={generating || prompt.trim().length === 0}
      >
        {generating ? "Generating..." : "Generate formula"}
      </button>
      {aiError && <div className="formula-status">{aiError}</div>}
    </div>
  );
}
